import React, { useState, useContext, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { StartAuthLoader, StartAuthTitleContent } from '../components';
import { LogosRow } from '../components/LogosRow';
import TextLabel from '../components/FormComponents/TextLabel';
import { BTN_ACTIONS } from '../../utils';
import { ConnectorContext } from '../../context';
import { getAuthorizeUrl } from '../../fetch/requests';
import useTrackingEvents from '../hooks/useTrackingEvents';
import useSendTrackingEvents from '../hooks/useSendTrackingEvents';

const TIMEOUT_DELAY = 3000;

export const StartAuthorize = () => {
  const [isStartedConnection, setIsStartedConnection] = useState(false);
  const [isStartSendingEvents, setIsStartSendingEvents] = useState(false);
  const [isEventsSent, setIsEventsSent] = useState(false);

  const navigate = useNavigate();
  const { search } = useLocation();
  const {
    state: {
      currentApp: currentApplication,
      currentApp: { steps, id, name: applicationName, applicationLogoUrl },
      clientId,
      state,
      preStartConnection,
      applicationData,
      triggeredEvents,
      trackingId,
    },
    updateState,
  } = useContext(ConnectorContext);

  const stepsLength = Object.keys(steps).length;

  useEffect(() => {
    if (preStartConnection.isReadyToConnect && isEventsSent) {
      setTimeout(() => {
        window.location.href = preStartConnection.redirectUrl;
      }, TIMEOUT_DELAY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preStartConnection.isReadyToConnect, isEventsSent]);

  useEffect(() => {
    if (preStartConnection.isReadyToConnect) {
      setIsStartSendingEvents(true);
    }
  }, [preStartConnection.isReadyToConnect]);

  useTrackingEvents({
    currentApp: currentApplication,
    triggeredEvents,
    clientId,
    trackingId: preStartConnection?.trackingId || trackingId,
    updateState,
  });

  useEffect(() => {
    if (triggeredEvents?.length && preStartConnection?.isReadyToConnect) {
      if (preStartConnection?.trackingId) {
        updateState({ trackingId: preStartConnection?.trackingId });
        sessionStorage.setItem('trackingId', preStartConnection?.trackingId);
      }
      setIsStartSendingEvents(true);
    }
  }, [triggeredEvents, preStartConnection.isReadyToConnect]);

  useSendTrackingEvents({
    isStartSendingEvents,
    triggeredEvents,
    trackingId: preStartConnection?.trackingId || trackingId,
    clientId,
    setIsEventsSent,
  });

  // The last step is the button that starts the connection. An application can
  // put more on it: the lines that tell what the button does, the logo of the other
  // system and a Back button; then the screen is the one that tells before it starts.
  const lastStep = steps[stepsLength];
  const findButton = (action) => lastStep.find((item) => item.action === action);
  const startConnectionBtn = findButton(BTN_ACTIONS.COMPLETE) || lastStep[0];
  const lines = lastStep.filter((item) => item.type === 'label');
  const otherLogos = lastStep.filter((item) => item.type === 'logo');
  const backBtn = findButton(BTN_ACTIONS.BACK);

  const onStartConnection = () => {
    setIsStartedConnection(!isStartedConnection);
    const requestBody = {
      application_id: id,
      client_id: clientId,
      application_data: applicationData || {},
      state,
    };
    getAuthorizeUrl({ updateState, requestBody });
  };

  const renderBtn = () => (
    <Button
      variant="contained"
      sx={{ marginTop: '10px', color: '#fff' }}
      color="blackBtn"
      onClick={onStartConnection}
      disabled={isStartedConnection}
      fullWidth
      id={startConnectionBtn?.name || 'redirect_btn'}
      data-testid={startConnectionBtn?.name || 'redirect_btn'}
    >
      <FormattedMessage id={startConnectionBtn.displayName} />
    </Button>
  );

  if (lines.length && !isStartedConnection) {
    return (
      <Grid container item justifyContent="center" direction="column" sx={{ margin: '0 auto' }}>
        <LogosRow
          logos={[
            { url: applicationLogoUrl, alt: applicationName },
            ...otherLogos.map((logo) => ({ url: logo.url, alt: '' })),
          ]}
        />

        <StartAuthTitleContent isStartedConnection={false} />

        <Box sx={{ paddingBottom: '20px' }} data-testid="start_lines">
          {lines.map((line) => (
            <TextLabel key={line.index} field={line} dataTestid={`label_${line.index}`} />
          ))}
        </Box>

        <Grid>{renderBtn()}</Grid>

        {backBtn ? (
          <Button
            variant="text"
            onClick={() => navigate(`/connect/step/${stepsLength - 1}${search}`)}
            sx={{ marginTop: '10px', textTransform: 'none' }}
            fullWidth
            data-testid="back_btn"
          >
            <FormattedMessage id="back" />
          </Button>
        ) : null}
      </Grid>
    );
  }

  return (
    <Grid container item justifyContent="center" direction="column" sx={{ margin: '0 auto' }}>
      <StartAuthTitleContent isStartedConnection={isStartedConnection} />

      <StartAuthLoader isStartedConnection={isStartedConnection} />

      <Grid>{renderBtn()}</Grid>
    </Grid>
  );
};
